import { useFlags } from 'flagsmith/react';

import { FEATURE_FLAG_DEFAULTS, FEATURE_FLAGS, type FeatureFlag } from '@/constants/featureFlags';
import { getFeatureFlagOverride, getFeatureFlagValueOverride } from '@/util/testOverrides';

export { getFeatureFlagValue } from '@/util/featureFlags';

/**
 * Hook to get a boolean feature flag value.
 *
 * Returns the evaluated value from Flagsmith, or FEATURE_FLAG_DEFAULTS
 * when the flag is missing or enabled is undefined/null.
 *
 * @param flag - Feature flag key from FEATURE_FLAGS
 * @returns boolean flag value
 *
 * @example
 * const isInquiryLabelsEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_LABELS);
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  const flags = useFlags([flag]);

  return resolveEnabled(flag, flags[flag]);
}

type FlagsmithFeature = { enabled?: boolean | null; value?: unknown } | undefined;

type FeatureFlagValue = string | boolean | Record<string, unknown>;

function resolveEnabled(flag: FeatureFlag, feature: FlagsmithFeature): boolean {
  const override = getFeatureFlagOverride(flag);

  if (override !== undefined) {
    return override;
  }

  if (!feature || feature.enabled === undefined || feature.enabled === null) {
    return Boolean(FEATURE_FLAG_DEFAULTS[flag]);
  }

  return feature.enabled;
}

function resolveValue(flag: FeatureFlag, feature: FlagsmithFeature): FeatureFlagValue {
  const override = getFeatureFlagValueOverride(flag);

  if (override !== undefined) {
    return override;
  }

  if (!feature || feature.value === undefined || feature.value === null) {
    return FEATURE_FLAG_DEFAULTS[flag];
  }

  const value = feature.value;

  if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
    try {
      return JSON.parse(value) as Record<string, unknown>;
    } catch {
      return value;
    }
  }

  return value as boolean | string;
}

/**
 * Hook to get a feature flag's value (for multivariate flags).
 *
 * Returns the evaluated flag value from Flagsmith,
 * or the default value from FEATURE_FLAG_DEFAULTS if the provider is unavailable or evaluation fails.
 * JSON strings are automatically parsed into objects.
 *
 * @param flag - Feature flag key from FEATURE_FLAGS
 * @returns The flag's value, or default if unavailable
 *
 * @example
 * const variant = useFeatureFlagValue(FEATURE_FLAGS.AVENTRIA_LANDING_PAGE);
 */
export function useFeatureFlagValue(flag: FeatureFlag): FeatureFlagValue {
  const flags = useFlags([flag]);

  return resolveValue(flag, flags[flag]);
}

export type ResolvedFeatureFlag = { flag: FeatureFlag; enabled: boolean; value: FeatureFlagValue };

const ALL_FEATURE_FLAGS = Object.values(FEATURE_FLAGS);

/**
 * Every flag the app knows, resolved the way `useFeatureFlag` and
 * `useFeatureFlagValue` resolve each one: test override, then Flagsmith, then
 * the default. For the Primum Settings screen, so it shows what the app sees
 * rather than what Flagsmith alone returns.
 */
export function useAllFeatureFlags(): ResolvedFeatureFlag[] {
  const flags = useFlags(ALL_FEATURE_FLAGS);

  return ALL_FEATURE_FLAGS.map((flag) => ({
    flag,
    enabled: resolveEnabled(flag, flags[flag]),
    value: resolveValue(flag, flags[flag]),
  }));
}
